"use client";
import { nanoid } from "@/lib/utils";
import { auth } from "@/lib/firebase";
import type { Question } from "@/types";

export const AI_WORKER_URL = "https://polished-shadow-f08c.yassow.workers.dev/";
// Legacy EmailJS identifiers. No longer used to send anything - all mail now
// goes through the Worker (Resend). Kept only so nothing importing them breaks.
export const EMAILJS_SERVICE_ID = "service_pu433a4";
export const EMAILJS_TEMPLATE_ID = "template_l62666k";
export const EMAILJS_PUBLIC_KEY = "tSIOLMDkcK9CCwiiJ";

export const AI_ENABLED = AI_WORKER_URL.length > 0;
export const EMAIL_ENABLED = AI_WORKER_URL.length > 0;

export async function generateQuestions(
  topic: string,
  count: number,
  language: "en" | "ar" | "uk",
  avoid: string[] = [],
  source: string = ""
): Promise<Question[]> {
  const r = await fetch(AI_WORKER_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ topic, count, language, avoid, source, email: (auth.currentUser && auth.currentUser.email) || "" }),
  });
  let data: any = {};
  try { data = await r.json(); } catch { /* ignore */ }
  if (!r.ok || data.error) {
    throw new Error(String(data.detail || data.error || ("AI request failed (" + r.status + ")")));
  }
  const arr = Array.isArray(data.questions) ? data.questions : [];
  const norm = (t: string) =>
    String(t || "").toLowerCase().replace(/[^a-z0-9\u0600-\u06ff]+/g, " ").trim();
  const seen = new Set((avoid || []).map(norm).filter(Boolean));
  const out: Question[] = [];
  for (const q of arr) {
    const text = String(q.text || "").slice(0, 150);
    const key = norm(text);
    if (!key || seen.has(key)) continue; // skip blanks and duplicates
    seen.add(key);
    const options = Array.isArray(q.options)
      ? q.options.slice(0, 6).map((o: any) => String(o).slice(0, 75))
      : ["", "", "", ""];
    while (options.length < 2) options.push("");
    const ci = Number.isInteger(q.correctIndex)
      ? Math.max(0, Math.min(q.correctIndex, options.length - 1))
      : 0;
    out.push({
      id: nanoid(),
      text,
      options,
      correctAnswer: ci,
      correctAnswers: [ci],
      multiSelect: false,
      type: "multiple",
      timeLimit: 20,
      points: 1000,
    });
  }
  return out;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Basic shape check for an address a player typed in. */
export function isValidEmail(v: string | null | undefined): boolean {
  const s = String(v || "").trim();
  return s.length > 0 && s.length <= 254 && EMAIL_RE.test(s);
}

// All outbound mail goes through the Worker (Resend), so it is sent from our
// own domain, is not capped by a third-party free tier, and - unlike the old
// browser-side EmailJS path - cannot be blocked by the visitor's ad blocker.
async function workerSend(to: string, subject: string, html: string): Promise<void> {
  const body = JSON.stringify({ mode: "email", to: to, subject: subject, html: html, email: to });
  let lastErr: any = null;
  // iOS Safari in particular surfaces a transient "Load failed" TypeError when a
  // reused connection has gone stale (e.g. the page sat idle) or on a brief network
  // blip. A best-effort email should not give up on that, so retry a couple of times
  // with backoff and a timeout, and only fail fast on a real client (4xx) error.
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt > 0) await new Promise((res) => setTimeout(res, 600 * attempt));
    let timer: any = null;
    let r: Response;
    try {
      const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      if (controller) timer = setTimeout(() => controller.abort(), 15000);
      r = await fetch(AI_WORKER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body,
        signal: controller ? controller.signal : undefined,
      });
      if (timer) clearTimeout(timer);
    } catch (e: any) {
      if (timer) clearTimeout(timer);
      lastErr = e; // network error or timeout (abort) - retry
      continue;
    }
    if (r.ok) return;
    if (r.status >= 500 || r.status === 429) { lastErr = new Error("email send failed (HTTP " + r.status + ")"); continue; }
    let detail = "";
    try { detail = (await r.text()).slice(0, 300); } catch (e) { /* ignore */ }
    throw new Error("email send failed (HTTP " + r.status + ") " + detail);
  }
  throw lastErr || new Error("email send failed");
}

export async function sendAssignmentEmail(params: {
  toEmail: string;
  ccEmail?: string;
  quizTitle: string;
  playerName: string;
  score: number;
  correctCount: number;
  totalQuestions: number;
}): Promise<void> {
  if (!params.toEmail) return;
  if (!isValidEmail(params.toEmail)) throw new Error("invalid recipient address");
  const completed = new Date().toLocaleString();
  const subject = "QuizUps results: " + params.quizTitle;
  const message =
    params.playerName + ' completed your quiz "' + params.quizTitle + '".<br><br>' +
    "Score: " + params.score.toLocaleString() + " points<br>" +
    "Correct answers: " + params.correctCount + " / " + params.totalQuestions + "<br>" +
    "Completed: " + completed + "<br><br>- Sent by QuizUps";
  await workerSend(params.toEmail.trim(), subject, message);
  const cc = String(params.ccEmail || "").trim();
  if (cc && cc !== params.toEmail.trim() && isValidEmail(cc)) {
    await workerSend(cc, subject, message);
  }
}

export async function sendCollabInvite(params: {
  toEmail: string;
  inviterName: string;
  quizTitle: string;
  role: string;
  kind: string;
  link: string;
}): Promise<void> {
  if (!isValidEmail(params.toEmail)) throw new Error("invalid recipient address");
  const what = params.kind === "poll" ? "poll" : "quiz";
  const can = params.role === "edit" ? "edit it and run it live" : "run it live with a group";
  const subject = params.inviterName + " invited you to work on \"" + params.quizTitle + "\"";
  const message =
    "<p style=\"font-size:17px\"><strong>" + params.inviterName + "</strong> would like your help with a " + what + " on QuizUps.</p>" +
    "<p style=\"font-size:20px;font-weight:bold;margin:18px 0 4px\">" + params.quizTitle + "</p>" +
    "<p style=\"color:#555;margin-top:0\">You can " + can + " whenever you like. Nothing to install, nothing to pay for.</p>" +
    "<p style=\"margin:26px 0\"><a href=\"" + params.link + "\" style=\"background:#5333e8;color:#fff;text-decoration:none;font-weight:bold;padding:13px 26px;border-radius:10px;display:inline-block\">Open the " + what + "</a></p>" +
    "<p style=\"color:#555\">New to QuizUps? Sign up with <strong>" + params.toEmail + "</strong> and verify your address - this " + what + " will be waiting on your dashboard. It is free, with no player limits and no ads.</p>" +
    "<p style=\"color:#999;font-size:13px;margin-top:26px\">If you were not expecting this, you can ignore the message and nothing will happen.</p>";
  await workerSend(params.toEmail.trim(), subject, message);
}

export async function sendAssignmentInvite(params: {
  toEmails: string[];
  quizTitle: string;
  link: string;
}): Promise<number> {
  const subject = "You're invited: " + params.quizTitle + " (QuizUps quiz)";
  const message =
    'You have been invited to take the quiz "' + params.quizTitle + '" on QuizUps.<br><br>' +
    '👉 Start here: <a href="' + params.link + '">' + params.link + "</a><br><br>" +
    "You can complete it any time, at your own pace.";
  const base: Record<string, string> = {
    subject,
    message,
    quiz_title: params.quizTitle,
    player_name: "A quiz host",
    score: "",
    correct_count: "",
    total_questions: "",
    completed_at: "",
  };
  let sent = 0;
  for (const to of params.toEmails) {
    const t = to.trim();
    if (!t) continue;
    try {
      const r = await fetch(AI_WORKER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "email", to: t, subject: subject, html: message }),
      });
      if (r.ok) sent++;
    } catch (e) {}
  }
  return sent;
}


export async function sealExam(items: any[]): Promise<string | null> {
  try {
    const r = await fetch(AI_WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mode: "seal", items }) });
    if (!r.ok) return null;
    const j = await r.json();
    return j.sealed || null;
  } catch { return null; }
}

export async function gradeExam(sealed: string, answers: any[]): Promise<{ correctCount: number; total: number; score: number }> {
  const r = await fetch(AI_WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mode: "grade", sealed, answers }) });
  if (!r.ok) throw new Error("grade failed");
  return await r.json();
}


export async function generateVideoQuestions(
  url: string,
  from: number,
  to: number,
  count: number,
  language: "en" | "ar" | "uk" = "en"
): Promise<Question[]> {
  const r = await fetch(AI_WORKER_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mode: "videoq", url, from, to, count, language }),
  });
  let data: any = {};
  try { data = await r.json(); } catch {}
  if (!r.ok || data.error) throw new Error(String(data.detail || data.error || "Video analysis failed"));
  const arr = Array.isArray(data.questions) ? data.questions : [];
  const out: Question[] = [];
  for (const q of arr) {
    const text = String(q.text || "").slice(0, 150);
    if (!text) continue;
    const options = Array.isArray(q.options) ? q.options.slice(0, 6).map((o: any) => String(o).slice(0, 75)) : ["", "", "", ""];
    while (options.length < 2) options.push("");
    const ci = Number.isInteger(q.correctIndex) ? Math.max(0, Math.min(q.correctIndex, options.length - 1)) : 0;
    out.push({ id: nanoid(), text, options, correctAnswer: ci, correctAnswers: [ci], multiSelect: false, type: "multiple", timeLimit: 20, points: 1000 });
  }
  return out;
}


export async function generateFromUrl(
  url: string,
  count: number,
  language: "en" | "ar" | "uk",
  avoid: string[] = []
): Promise<Question[]> {
  const r = await fetch(AI_WORKER_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mode: "urlq", url, count, language, avoid }),
  });
  let data: any = {};
  try { data = await r.json(); } catch {}
  if (!r.ok || data.error) throw new Error(String(data.detail || data.error || "URL analysis failed"));
  const arr = Array.isArray(data.questions) ? data.questions : [];
  const out: Question[] = [];
  for (const q of arr) {
    const text = String(q.text || "").slice(0, 150);
    if (!text) continue;
    const options = Array.isArray(q.options) ? q.options.slice(0, 6).map((o: any) => String(o).slice(0, 75)) : ["", "", "", ""];
    while (options.length < 2) options.push("");
    const ci = Number.isInteger(q.correctIndex) ? Math.max(0, Math.min(q.correctIndex, options.length - 1)) : 0;
    out.push({ id: nanoid(), text, options, correctAnswer: ci, correctAnswers: [ci], multiSelect: false, type: "multiple", timeLimit: 20, points: 1000 });
  }
  return out;
}


export async function uploadImage(blob: Blob): Promise<string> {
  const url = AI_WORKER_URL.replace(/\/$/, "") + "/upload";
  const attempts = 3;
  let lastErr: any = null;
  for (let i = 0; i < attempts; i++) {
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), 45000) : null;
    try {
      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": blob.type || "image/jpeg" },
        body: blob,
        signal: ctrl ? ctrl.signal : undefined,
      });
      if (timer) clearTimeout(timer);
      const j: any = await r.json().catch(() => ({}));
      if (r.ok && j.url) return j.url as string;
      lastErr = new Error(j.error || ("upload failed (" + r.status + ")"));
      // Only transient server states are worth retrying; a 4xx will fail again.
      if (r.status < 500 && r.status !== 429) throw lastErr;
    } catch (e: any) {
      if (timer) clearTimeout(timer);
      lastErr = e;
    }
    if (i < attempts - 1) await new Promise((res) => setTimeout(res, 600 * (i + 1)));
  }
  const raw = String((lastErr && (lastErr.message || lastErr.name)) || "");
  const msg = (lastErr && lastErr.name === "AbortError")
    ? "Image upload timed out. Please check your connection and try again."
    : /Failed to fetch|NetworkError|load failed|ERR_/i.test(raw)
      ? "Image upload failed — a network issue or a browser/ad blocker stopped it. Check your connection (and disable any blocker for quizups.com), then try again."
      : (raw || "upload failed");
  throw new Error(msg);
}
